import { Box, Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { DEFAULT_VARIANT_TITLE, NO_OP_CALLBACK } from "@smartrr/shared/constants";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusQuantity } from "@smartrr/shared/customerPortal/components/AquariusQuantity";
import { SellingPlanConfigs } from "@smartrr/shared/entities/SellingPlanGroup";
import { extractShopifyId } from "@smartrr/shared/utils/ensureShopifyGid";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import isEqual from 'lodash/isEqual';
import truncate from 'lodash/truncate';
import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useSmartrrTheme } from "@customer-app/account/views/OperationAquarius/hooks/useSmartrrTheme";
import { useOpenModal } from "@customer-app/state/actionCreators/modal";
import {
  VariantPurchaseOptions,
  useVariantEnablementForSellingPlanId,
} from "@customer-app/state/reducers/sellingPlanGroups";

import { AddonPricing } from "./AddonPricing";
import { OrderItemImage, VerticalProductWrapper } from "../../../constants";
import { Selector } from "../../../Modals/components/Selectors/Selector";
import { INITIAL_ADDON_QUANTITY, useActiveAddon } from "../hooks/useActiveAddon";
import { AddonItemProps } from "../types";
import { getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";

export const AddonItem = React.memo(
  ({
    addonProduct,
    currency,
    isLoading: isParentLoading,
    sellingPlanId,
    portalNodeEle,
    onAddItemSubmit,
    delivery,
    renderItemsForSubscription,
    maxQuantity,
    selectOnClick,
    configType,
    returnToAddonList,
  }: AddonItemProps) => {
    const [selectedVariantOptionLocal, setSelectedVariantOptionLocal] = useState<
      VariantPurchaseOptions | undefined
    >();
    const { activeAddonId, activeAddonQuantity, setActiveAddonId, setActiveAddonQuantity } = useActiveAddon();
    const [showQuantitySelector, setShowQuantitySelector] = useState(false);
    const [isActive, setIsActive] = useState<boolean>(false);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(isParentLoading);
    const theme = useSmartrrTheme();
    const openModal = useOpenModal();

    const { modernThemeSettings: themeSettings } = theme;

    const shopifyProductId = addonProduct?.shopifyId ? extractShopifyId(addonProduct.shopifyId) : "";

    const checkIfActive = () => (activeAddonId ? addonProduct.id === activeAddonId : false);
    const themeToggleShowVariantName =
      themeSettings?.subscriptionActions.showVariantName && themeSettings?.showHide.showVariantName;
    const enabledVariantOptions = useVariantEnablementForSellingPlanId(
      addonProduct!,
      sellingPlanId,
      !renderItemsForSubscription && configType !== SellingPlanConfigs.SUBSCRIPTION,
      configType === SellingPlanConfigs.TRENDING
    );

    const filteredVariants = enabledVariantOptions.filter(option =>
      renderItemsForSubscription ? !!option.sellingPlan : !!option[configType]
    );

    const productHasSingleVariableAvailable =
      filteredVariants.length === 1 && filteredVariants[0].vnt.purchasableVariantName !== DEFAULT_VARIANT_TITLE;

    const addonStatusToggle = useCallback(
      (active: boolean) => {
        setShowQuantitySelector(active);
        setIsActive(active);
        if (active) {
          setActiveAddonId(addonProduct.id);
          setActiveAddonQuantity(INITIAL_ADDON_QUANTITY);
        } else {
          setActiveAddonId(null);
        }
      },
      [addonProduct]
    );

    const onConfirm = useCallback(() => {
      setTimeout(() => {
        setShowConfirm(false);
        setIsLoading(false);
        setActiveAddonId(null);
      }, 2000);
    }, []);

    useEffect(() => {
      /** this useEffect handles cases where the user is swapping between different addons by clicking 'Add'
       *  we need to change the state of the current addon to be inactive so the newly selected item is active
       */
      if ((showQuantitySelector && !checkIfActive()) || activeAddonQuantity < 1) {
        setShowQuantitySelector(false);
        setIsActive(false);

        // if the quantity drops below 1 then we need to make current item inactive AND remove the id from activeAddonId
        activeAddonQuantity < 1 && setActiveAddonId(null);
      }
    }, [activeAddonId, activeAddonQuantity]);

    const openDescriptionModal = useCallback(
      () =>
        openModal(
          "PRODUCT_DESCRIPTION",
          {
            product: addonProduct,
            configType,
            currency,
            onAddItemSubmit,
            sellingPlanId,
            maxQuantity,
            deliveryId: getDeliveryId(delivery),
            returnToModal: returnToAddonList,
          },
          {
            contained: false,
          }
        ),
      [addonProduct, openModal]
    );

    if (!filteredVariants.length) {
      return <React.Fragment />;
    }

    return (
      <VerticalProductWrapper
        item
        className={
          addonProduct.id === activeAddonId ? prefixClassName("addon-selected") : prefixClassName("addon")
        }
        data-smartrr-shopify-product-id={String(shopifyProductId)}
        data-testid={`${isActive ? "active-" : ""}addon-item`}
        theme={theme}
        onClick={() => (selectOnClick && !isActive ? addonStatusToggle(true) : NO_OP_CALLBACK)}
        sx={{
          ...(selectOnClick && { cursor: "pointer" }),
        }}
      >
        <Stack sx={{ position: "relative" }}>
          <OrderItemImage
            purchasable={addonProduct}
            {...(!!selectedVariantOptionLocal?.vnt && { purchasableVariant: selectedVariantOptionLocal?.vnt })}
            size="medium"
            onClick={openDescriptionModal}
            sx={{ cursor: "pointer" }}
          />
          {showQuantitySelector ? (
            <React.Fragment>
              <AquariusQuantity
                className={prefixClassName("edit-item-quantity-container")}
                value={activeAddonQuantity}
                isLoading={isLoading}
                onChange={setActiveAddonQuantity}
                maxQty={maxQuantity}
              />
            </React.Fragment>
          ) : selectOnClick ? null : (
            <AquariusButton onClick={() => addonStatusToggle(true)}>
              {typedLocalize("general.add")}
            </AquariusButton>
          )}
          <Typography variant="body1" className={prefixClassName("product-name")} style={{ marginTop: "7px" }}>
            {truncate(addonProduct.purchasableName)}
          </Typography>
          <Box display="flex" flexDirection="column">
            {themeToggleShowVariantName && productHasSingleVariableAvailable ? (
              <Box display="flex" sx={{ marginBottom: "3px" }}>
                <Typography variant="caption1" className={prefixClassName("product-variant-name")}>
                  {truncate(filteredVariants[0].vnt.purchasableVariantName)}
                </Typography>
              </Box>
            ) : null}
            <AddonPricing
              enabledVariantOptions={enabledVariantOptions}
              filteredAddonVariants={filteredVariants}
              currency={currency}
              configType={configType}
              variantFromDropdown={selectedVariantOptionLocal?.vnt}
              delivery={delivery}
            />
          </Box>
          {/* TODO: Refactor this create portal so that it reads directly from context and doesn't need to be re-created with each product selection */}
          {isActive
            ? createPortal(
                <Selector
                  onSubmitHandler={enabledVariantOptions => {
                    setIsLoading(true);
                    onAddItemSubmit(
                      selectedVariantOptionLocal || enabledVariantOptions[0],
                      activeAddonQuantity
                    ).then(() => onConfirm());
                  }}
                  isLoading={isLoading}
                  onCancelHandler={() => addonStatusToggle(false)}
                  purchasable={addonProduct}
                  currency={currency}
                  sellingPlanId={sellingPlanId}
                  selectedVariantOption={selectedVariantOptionLocal!}
                  setSelectedVariantOption={setSelectedVariantOptionLocal}
                  showConfirmState={showConfirm}
                  options={{
                    includeAddons: configType === SellingPlanConfigs.ADDON,
                    includeTrending: configType === SellingPlanConfigs.TRENDING,
                  }}
                />,
                portalNodeEle
              )
            : null}
        </Stack>
      </VerticalProductWrapper>
    );
  }
);

export const MemoizedAddonItem = React.memo(AddonItem, (prev, current) =>
  isEqual(prev.addonProduct, current.addonProduct)
);
